import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface ArchiveProfessional {
  directory_slug: string;
  first_name: string;
  last_name: string;
  profession: string;
}

/**
 * Crawlable archive of ALL professional profiles.
 * Renders every approved professional as a plain <a> link so search engine
 * crawlers can discover profile pages that live beyond the paginated directory.
 */
const ProfessionalArchiveLinks = () => {
  const { data: professionals } = useQuery<ArchiveProfessional[]>({
    queryKey: ['professional-archive-links'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('get-professional-directory', {
        body: {
          search: '',
          location: '',
          radius: 0,
          licenseVerifiedOnly: false,
          sortBy: 'best_match',
          excludeCaregivers: false,
          profession: 'all',
          filters: {},
        },
      });

      if (error) throw error;
      return ((data?.professionals || []) as ArchiveProfessional[])
        .filter((p) => p.directory_slug)
        .sort((a, b) =>
          `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`)
        );
    },
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  if (!professionals || professionals.length === 0) return null;

  return (
    <section className="mt-12 border-t pt-8">
      <h2 className="text-2xl font-bold text-foreground mb-2">Browse All Professionals</h2>
      <p className="text-muted-foreground mb-6">
        All {professionals.length} verified aging-in-place professionals in our network.
      </p>
      <ul className="columns-1 sm:columns-2 lg:columns-3 gap-x-8 text-sm">
        {professionals.map((pro) => (
          <li key={pro.directory_slug} className="mb-1.5 break-inside-avoid">
            <Link
              to={`/professionals/${pro.directory_slug}`}
              className="text-muted-foreground hover:text-primary hover:underline transition-colors"
            >
              {pro.first_name} {pro.last_name}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default ProfessionalArchiveLinks;
